import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router';
import cls from 'clsx';
import { Globe } from '@linktivity/link-icons';
import { Dropdown } from '@linktivity/link-ui';
import { ENGLISH_LOCALE, LOCALE_OPTIONS } from '@link/share/constants';
import { SHUTTO_LOCALE_VALUES } from '@link/skytree/i18n';
import {
  STT_LANG_PARAM,
  resolveSttLang,
  setSttLang,
  removeSttLang
} from '@link/skytree/routes/sttLang';
import { LocaleOption } from './header.types';
import styles from './header.module.css';

// Shutto Translation API type declaration
interface ShuttoAPI {
  set: (key: string, value: string) => void;
  get: (key: string, callback: (value: string) => void) => void;
}

declare global {
  interface Window {
    __stt?: ShuttoAPI;
  }
}

interface LocaleProps {
  localeList?: readonly LocaleOption[];
}

const Locale: FC<LocaleProps> = ({ localeList = LOCALE_OPTIONS }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openMenu, setOpenMenu] = useState(false);

  // The active Shutto language, resolved from the URL param or, when absent,
  // from session storage. Derived while rendering, not synced into state from
  // an effect: the loader and changeLocale write session storage before a
  // navigation commits, so the render of the new location already sees the new
  // language. Syncing it an effect later let the Shutto effect below run once
  // with the previous language and reset Shutto, e.g. on back/forward.
  // Keyed on location, whose identity changes on every navigation - including a
  // replace to the same URL - because the stored value can change while the
  // param stays absent.
  const sttLang = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return resolveSttLang(params.get(STT_LANG_PARAM));
  }, [location]);

  // Determine current display language: sttLang takes priority over i18n.language
  const currentLang = useMemo(
    () => sttLang ?? i18n.language,
    [sttLang, i18n.language]
  );

  const currentLocaleLabel = useMemo(
    () => localeList.find(locale => locale.value === currentLang)?.label,
    [currentLang, localeList]
  );

  // Initialize Shutto translation on mount and whenever the active language changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (sttLang) {
      // A Shutto language is active - enable Shutto translation
      if (window.__stt) {
        window.__stt.set('lang', sttLang);
      }
    } else {
      // No Shutto language - reset Shutto and use i18n
      // Delete Shutto cookie to prevent interference with i18n translations
      document.cookie =
        'stt_lang=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      // Reset Shutto to base language (URL locale)
      if (window.__stt) {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const urlLocale = pathSegments[0] || i18n.language;
        window.__stt.set('lang', urlLocale);
      }
    }
  }, [sttLang, location.pathname, i18n.language]);

  const changeLocale = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, locale: string) => {
      e.preventDefault();
      setOpenMenu(false);

      const isShuttoLocale = (
        SHUTTO_LOCALE_VALUES as readonly string[]
      ).includes(locale);

      if (isShuttoLocale) {
        // Switching to a Shutto language - persist in sessionStorage
        setSttLang(locale);
        // Shutto translates from English, so navigate to /en/ path
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const urlLocale = pathSegments[0];

        if (urlLocale !== ENGLISH_LOCALE) {
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set(STT_LANG_PARAM, locale);
          const queryString = newSearchParams.toString();
          const searchSuffix = queryString ? `?${queryString}` : '';
          const newPath = location.pathname.replace(
            `/${urlLocale}`,
            `/${ENGLISH_LOCALE}`
          );
          navigate(`${newPath}${searchSuffix}`, {
            replace: true,
            state: location.state
          });
        } else {
          setSearchParams(
            prev => {
              const newParams = new URLSearchParams(prev);
              newParams.set(STT_LANG_PARAM, locale);
              return newParams;
            },
            { replace: true, state: location.state }
          );
        }

        // Immediately call Shutto API to trigger translation
        if (window.__stt) {
          window.__stt.set('lang', locale);
        }
        return;
      }

      // Switching to an i18n language - clear sessionStorage
      removeSttLang();
      // Remove sttLang param from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(STT_LANG_PARAM);
      const queryString = newSearchParams.toString();
      const searchSuffix = queryString ? `?${queryString}` : '';

      const pathSegments = location.pathname.split('/').filter(Boolean);
      const urlLocale = pathSegments[0];

      // Check if we're switching back to the same language as the URL
      // This happens when switching from Shutto language back to original i18n language
      if (urlLocale === locale) {
        // Just remove the sttLang param, no need to navigate
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            newParams.delete(STT_LANG_PARAM);
            return newParams;
          },
          { replace: true, state: location.state }
        );
        return;
      }

      // Navigate to new path with updated locale
      const newPath = location.pathname.replace(`/${urlLocale}`, `/${locale}`);
      navigate(`${newPath}${searchSuffix}`, { state: location.state });
    },
    [location, navigate, searchParams, setSearchParams]
  );

  const localeButton = useMemo(
    () => (
      <button
        className={styles.navButton}
        type="button"
        onClick={() => setOpenMenu(visible => !visible)}
      >
        <Globe className={styles.navIcon} />
        <span className={styles.text} data-stt-ignore>
          {currentLocaleLabel}
        </span>
      </button>
    ),
    [currentLocaleLabel]
  );

  return (
    <Dropdown
      target={localeButton}
      visible={openMenu}
      sameWidth={false}
      onClose={() => setOpenMenu(false)}
    >
      <Dropdown.Menu className={styles.dropdownMenu}>
        {localeList.map(locale => (
          <Dropdown.Item key={locale.value}>
            <a
              data-stt-changelang={locale.value}
              data-stt-ignore
              className={cls(styles.dropdownButton, {
                [styles.active]: locale.value === currentLang,
                [styles.disabled]: !!locale.disabled
              })}
              onClick={e => !locale.disabled && changeLocale(e, locale.value)}
            >
              {locale.label}
            </a>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Locale;
